import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import RichEditor from "../components/RichEditor";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiEdit2, FiTrash2, FiPlus, FiX, FiExternalLink } from "react-icons/fi";

const toSlug = (title) =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const emptyForm = { title: "", slug: "", content: "", metaDescription: "" };

const Pages = () => {
  const [pages, setPages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slugManual, setSlugManual] = useState(false);

  const fetchData = async () => {
    try {
      const snap = await getDocs(query(collection(db, "pages"), orderBy("createdAt", "desc")));
      setPages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleTitleChange = (val) => {
    setForm((f) => ({
      ...f,
      title: val,
      slug: slugManual ? f.slug : toSlug(val),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim()) { alert("Title and slug are required."); return; }
    setSaving(true);
    try {
      const data = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        content: form.content,
        metaDescription: form.metaDescription,
      };
      if (editId) {
        await updateDoc(doc(db, "pages", editId), { ...data, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "pages"), { ...data, createdAt: serverTimestamp() });
      }
      setShowForm(false); setForm(emptyForm); setEditId(null); setSlugManual(false);
      fetchData();
    } catch (err) { alert("Error: " + err.message); }
    setSaving(false);
  };

  const handleEdit = (page) => {
    setForm({ title: page.title || "", slug: page.slug || "", content: page.content || "", metaDescription: page.metaDescription || "" });
    setEditId(page.id); setSlugManual(true); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this page? This cannot be undone.")) return;
    await deleteDoc(doc(db, "pages", id));
    fetchData();
  };

  const openNew = () => { setForm(emptyForm); setEditId(null); setSlugManual(false); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(emptyForm); setEditId(null); setSlugManual(false); };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Pages</h1>
            <p className="text-sm text-gray-400">Create and manage custom pages (About, Resources, etc.)</p>
          </div>
          <button onClick={openNew}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#e84fa5] text-white font-bold uppercase text-[13px] rounded-lg hover:bg-pink-600 transition">
            <FiPlus /> Add Page
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
          <p className="text-[13px] text-blue-700 font-semibold mb-1">How custom pages work</p>
          <p className="text-[12px] text-blue-600">Create a page here, then go to <strong>Navigation Manager</strong> to add it to the header or footer menu. Pages are accessible at <code className="bg-blue-100 px-1 rounded">/page/your-slug</code></p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div></div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Page Title</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">URL Slug</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Last Updated</th>
                  <th className="text-right px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pages.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-10 text-gray-400 text-sm">No custom pages yet. Create your first page!</td></tr>
                )}
                {pages.map((page) => (
                  <tr key={page.id} className="border-b hover:bg-gray-50 transition">
                    <td className="px-5 py-4 font-semibold text-[13px] text-black">{page.title}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        /page/{page.slug}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-[12px] text-gray-400">
                      {page.updatedAt?.toDate
                        ? page.updatedAt.toDate().toLocaleDateString()
                        : page.createdAt?.toDate
                          ? page.createdAt.toDate().toLocaleDateString()
                          : "—"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a href={`/page/${page.slug}`} target="_blank" rel="noopener noreferrer"
                          className="p-2 text-green-500 hover:bg-green-50 rounded-lg transition" title="View Page">
                          <FiExternalLink size={15} />
                        </a>
                        <button onClick={() => handleEdit(page)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition" title="Edit"><FiEdit2 size={15} /></button>
                        <button onClick={() => handleDelete(page.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition" title="Delete"><FiTrash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* FORM MODAL */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-6 px-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10 rounded-t-2xl">
                <h2 className="text-lg font-extrabold">{editId ? "Edit Page" : "Create New Page"}</h2>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-700 p-1"><FiX size={22} /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Page Title *</label>
                    <input required value={form.title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[14px] outline-none focus:border-[#e84fa5] transition"
                      placeholder="e.g. Our Team, FAQ, Resources" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">
                      URL Slug *
                      <span className="font-normal text-gray-400 ml-1">(auto-generated from title)</span>
                    </label>
                    <div className="flex items-center border border-gray-200 rounded-lg focus-within:border-[#e84fa5] transition overflow-hidden">
                      <span className="px-3 py-2.5 bg-gray-50 text-gray-400 text-[13px] border-r border-gray-200">/page/</span>
                      <input value={form.slug}
                        onChange={(e) => { setSlugManual(true); setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })); }}
                        className="flex-1 px-3 py-2.5 text-[14px] outline-none"
                        placeholder="page-slug" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Meta Description (SEO)</label>
                  <input value={form.metaDescription}
                    onChange={(e) => setForm((f) => ({ ...f, metaDescription: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] transition"
                    placeholder="Brief description for search engines (optional)" />
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-2">Page Content *</label>
                  <RichEditor
                    value={form.content}
                    onChange={(val) => setForm((f) => ({ ...f, content: val }))}
                    placeholder="Write your page content here..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={closeForm}
                    className="px-6 py-2.5 border border-gray-200 rounded-lg text-[13px] font-semibold hover:bg-gray-50 transition">Cancel</button>
                  <button type="submit" disabled={saving}
                    className="px-8 py-2.5 bg-[#e84fa5] text-white font-bold text-[13px] rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
                    {saving ? "Saving..." : editId ? "Update Page" : "Publish Page"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Pages;
